import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Button,
	Col,
	Form,
	Input,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
	TreeSelect,
} from "antd";
import moment from "moment-jalaali";

import {
	CheckPermission,
	GetUrlParams,
	ListToTree,
	ObjectToURL,
} from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions, Status } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/JobForm";
import JobSubModal from "modals/JobSubscription";
import JobStatusModal from "modals/JobStatus";

const Component = ({
	cities,
	onSetRoute,
	parentName,
	permissions,
	routeName,
}) => {
	const initialState = {
		categories: [],
		subscriptions: [],
		data: [],
		pagination: {},
		users: [],
	};
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) =>
				(pagination.current - 1) * pagination.pageSize + index + 1,
		},
		{
			title: Resources.title,
			dataIndex: "title",
		},
		{
			title: Resources.owner,
			dataIndex: "user",
			className: "center-cell",
			render: (value) => value?.fullName,
		},
		{
			title: Resources.city,
			dataIndex: "city",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value, record) => {
				return (
					<>
						<span className="name">{value?.name}</span>
						<small className="desc">{record.province?.name}</small>
					</>
				);
			},
		},
		{
			title: Resources.subscription,
			dataIndex: "subscribedTill",
			className: "center-cell desc-cell color-cell ltr-cell",
			width: "100px",
			render: (value, record) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return value ? (
					record.subscribed ? (
						<>
							<span className="name green">{date}</span>
							<small className="desc green">{time}</small>
						</>
					) : (
						<>
							<span className="name yellow">{date}</span>
							<small className="desc yellow">{time}</small>
						</>
					)
				) : (
					<span className="red">{`${Resources.without} ${Resources.subscription}`}</span>
				);
			},
		},
		{
			title: Resources.status,
			dataIndex: "status",
			className: "center-cell color-cell",
			width: "100px",
			render: (value) => {
				switch (value) {
					case `${Status.job.approved}`: {
						return <span className="green">{Resources.approved}</span>;
					}
					case `${Status.job.neutral}`: {
						return <span className="yellow">{Resources.neutral}</span>;
					}
					case `${Status.job.pending}`: {
						return <span className="blue">{Resources.pending}</span>;
					}
					case `${Status.job.rejected}`: {
						return <span className="red">{Resources.rejected}</span>;
					}
					case `${Status.job.suspended}`: {
						return <span className="red">{Resources.suspended}</span>;
					}
				}
			},
		},
		{
			title: `${Resources.date} ${Resources.record}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "slug",
			className: "center-cell",
			width: "180px",
			render: (value, record) => (
				<>
					{permissions.status && (
						<Tooltip title={`${Resources.change} ${Resources.status}`}>
							<Button
								type="link"
								icon={<i className="fa fa-eye" />}
								onClick={() => handleStatus(record)}
							/>
						</Tooltip>
					)}
					{permissions.ladder && (
						<Tooltip
							title={`${Resources.activation} ${Resources.mode} ${Resources.ladder}${Resources.y}`}
						>
							<Button
								type="link"
								icon={<i className="fa fa-thumbtack" />}
								onClick={() => handleLadder(record)}
							/>
						</Tooltip>
					)}
					{permissions.attach && (
						<Tooltip title={`${Resources.attach} ${Resources.subscription}`}>
							<Button
								type="link"
								icon={<i className="fa fa-paperclip" />}
								onClick={() => handleSubscription(record)}
							/>
						</Tooltip>
					)}
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(record)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const {
		categories,
		data,
		loading,
		pagination,
		selected,
		subscriptions,
		users,
	} = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.job
			.list({ ...params, pageSize: process.env.REACT_APP_PAGE_SIZE })
			.then(({ data, meta: { currentPage, perPage, total } }) => {
				setState((prevState) => ({
					...prevState,
					data,
					pagination: {
						current: currentPage,
						pageSize: perPage,
						total,
						showSizeChanger: false,
					},
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleLadder = (selected) => {
		Modal.confirm({
			title: Resources.statusConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.job
					.ladder(selected.slug)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleStatus = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(`${pathname}${search}#${Modals.STATUS}`);
		}
	};

	const handleSubscription = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(`${pathname}${search}#${Modals.ATTACH}`);
		}
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleDelete = (selected) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.job
					.delete(selected.slug)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const query = ObjectToURL({ ...params, page: current });

		navigate(`${pathname}?${query}`);
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const handleUsers = debounce((search) => {
		if (search?.length > 1) {
			API.user
				.list({
					paginate: false,
					search,
				})
				.then(({ data }) => {
					setState((prevState) => ({
						...prevState,
						users: data.map((o) => ({
							value: o.id,
							label: o.fullName,
						})),
					}));
				})
				.catch(() => {});
		}
	}, 500);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });

		Promise.all([
			API.category.list({ paginate: false }),
			API.subscription.list({ paginate: false }),
			API.user.list({ pageSize: process.env.REACT_APP_PAGE_SIZE }),
		])
			.then(
				([{ data: categories }, { data: subscriptions }, { data: users }]) => {
					setState((prevState) => ({
						...prevState,
						categories: ListToTree(categories, true, true),
						subscriptions: subscriptions.map(({ id, title }) => ({
							label: title,
							value: id,
						})),
						users: users.map(({ id, fullName }) => ({
							label: fullName,
							value: id,
						})),
					}));
				}
			)
			.catch(() => {});
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={Resources.jobs}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							loading={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>
			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="user">
							<Select
								placeholder={Resources.user}
								options={users}
								onChange={handleFilter}
								onSearch={(e) => handleUsers(e)}
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="categoryId">
							<TreeSelect
								placeholder={Resources.category}
								treeData={categories}
								onChange={handleFilter}
								treeNodeFilterProp="label"
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="city">
							<Select
								placeholder={Resources.city}
								options={cities}
								onChange={handleFilter}
								optionFilterProp="label"
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="status">
							<Select
								placeholder={Resources.status}
								options={[
									{ value: "approved", label: Resources.approved },
									{ value: "neutral", label: Resources.neutral },
									{ value: "pending", label: Resources.pending },
									{ value: "rejected", label: Resources.rejected },
									{ value: "suspended", label: Resources.suspended },
								]}
								onChange={handleFilter}
								optionFilterProp="label"
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="subscribed">
							<Select
								placeholder={`${Resources.status} ${Resources.subscription}`}
								options={[
									{
										value: "true",
										label: `${Resources.have} ${Resources.subscription}`,
									},
									{
										value: "false",
										label: `${Resources.without} ${Resources.subscription}`,
									},
								]}
								onChange={handleFilter}
								optionFilterProp="label"
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="published">
							<Select
								placeholder={`${Resources.status} ${Resources.publish}`}
								options={[
									{
										value: "true",
										label: `${Resources.published}`,
									},
									{
										value: "false",
										label: `${Resources.unpublished}`,
									},
								]}
								onChange={handleFilter}
								optionFilterProp="label"
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 930 }}
				rowKey="id"
			/>
			<FormModal
				data={selected}
				onUpdate={handleForm}
				options={{ categories, cities }}
			/>
			<JobSubModal
				data={selected}
				onUpdate={handleSubscription}
				options={{ subscriptions }}
			/>
			<JobStatusModal
				data={selected}
				onUpdate={handleStatus}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {
			cities: state.provinces.map((province) => ({
				label: province.name,
				options: state.cities
					.filter((city) => Number(city.provinceId) === Number(province.id))
					.map(({ id, name }) => ({ value: id, label: name })),
			})),
			permissions: {
				create: CheckPermission(state.user.permissions, Permissions.JOB_CREATE),
				delete: CheckPermission(state.user.permissions, Permissions.JOB_DELETE),
				ladder: CheckPermission(state.user.permissions, Permissions.JOB_LADDER),
				status: CheckPermission(state.user.permissions, Permissions.JOB_STATUS),
				update: CheckPermission(state.user.permissions, Permissions.JOB_UPDATE),
				attach: CheckPermission(
					state.user.permissions,
					Permissions.SUBSCRIPTION_ATTACH
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
